import { useMemo, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import dayjs from "dayjs";
import { FilingReportItem, FilingReportStatusEntry } from "../../../api";

const VISIBLE_STATUS_COUNT = 2;

type FilingReportStatusHistoryCellProps = {
  row: FilingReportItem;
};

function formatStatusDate(value: string, compact = false) {
  return compact
    ? dayjs(value).format("MM/DD/YYYY")
    : dayjs(value).format("MM/DD/YYYY h:mm A");
}

function StatusHistoryEntry({
  entry,
  isLatest,
}: {
  entry: FilingReportStatusEntry;
  isLatest: boolean;
}) {
  const dateText = formatStatusDate(entry.updatedAt, !isLatest);
  const metaText = entry.updatedByName ? `${dateText} · ${entry.updatedByName}` : dateText;
  const fullText = `${entry.status} · ${metaText}`;

  return (
    <Typography
      variant="body2"
      noWrap
      title={fullText}
      sx={{
        fontSize: isLatest ? "0.8125rem" : "0.75rem",
        lineHeight: 1.5,
        color: isLatest ? "text.primary" : "text.secondary",
        pl: 1.25,
        position: "relative",
        "&::before": {
          content: '"•"',
          position: "absolute",
          left: 0,
          color: isLatest ? "primary.main" : "text.disabled",
          fontWeight: 700,
        },
      }}
    >
      <Box component="span" sx={{ fontWeight: isLatest ? 600 : 400 }}>
        {entry.status}
      </Box>
      <Box component="span" sx={{ fontWeight: 400 }}>
        {" · "}
        {metaText}
      </Box>
    </Typography>
  );
}

export default function FilingReportStatusHistoryCell({ row }: FilingReportStatusHistoryCellProps) {
  const [expanded, setExpanded] = useState(false);

  const history = useMemo(
    () =>
      [...(row.statusHistory ?? [])].sort(
        (a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf()
      ),
    [row.statusHistory]
  );

  const hiddenCount = Math.max(0, history.length - VISIBLE_STATUS_COUNT);
  const visibleEntries = expanded ? history : history.slice(0, VISIBLE_STATUS_COUNT);

  if (history.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 320 }}>
      {visibleEntries.map((entry, index) => (
        <StatusHistoryEntry
          key={`${row.taskId}-${entry.updatedAt}-${index}`}
          entry={entry}
          isLatest={index === 0}
        />
      ))}

      {hiddenCount > 0 && (
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{ alignSelf: "flex-start", fontWeight: 600, pl: 0.5 }}
        >
          {expanded ? "Show less" : `Show ${hiddenCount} more`}
        </Link>
      )}
    </Box>
  );
}
